import type { IAutoConfirmationRequestPayload } from "../types/chat";
import { EAutoCapability, EReasoningMode, ESearchMode } from "../types/chat";
import type {
  IAutoConfirmationCopyKeys,
  IAutoSendModes,
  IGetAutoConfirmationCopyKeysProps,
  IGetAutoConfirmationExpiryDelayProps,
  IIsAutoConfirmationForChatProps,
  IReduceAutoConfirmationProps,
  IResolveAutoSendModesProps,
} from "./auto-capabilities.types";

// NOTE: NSAI-6539 (contract C5) — what a send carries while Auto is selected.
// A mode the user left alone goes as AUTO, so the server's turn plan decides
// it; anything the user set is explicit and wins. With any other model the
// composer's values go unchanged. The full composer states are S7.
export const resolveAutoSendModes = ({
  isAutoModelSelected,
  searchMode,
  reasoningMode,
  isReasoningModeChosen = false,
  isSearchModeChosen = false,
}: IResolveAutoSendModesProps): IAutoSendModes => {
  if (!isAutoModelSelected) return { searchMode, reasoningMode };

  const isSearchLeftToAuto =
    searchMode === ESearchMode.NONE && !isSearchModeChosen;
  return {
    searchMode: isSearchLeftToAuto ? ESearchMode.AUTO : searchMode,
    reasoningMode: isReasoningModeChosen ? reasoningMode : EReasoningMode.AUTO,
  };
};

// NOTE: AUTO is a request value, never a state the composer shows: a chat
// restored with it reads as "nothing selected".
export const toComposerSearchMode = (searchMode: ESearchMode): ESearchMode =>
  searchMode === ESearchMode.AUTO ? ESearchMode.NONE : searchMode;

const AUTO_CONFIRMATION_COPY: Partial<
  Record<EAutoCapability, IAutoConfirmationCopyKeys>
> = {
  [EAutoCapability.DEEP_RESEARCH]: {
    descriptionKey: "auto_confirmation.deep_research",
    acceptKey: "auto_confirmation.accept_deep_research",
  },
  [EAutoCapability.DECK]: {
    descriptionKey: "auto_confirmation.deck",
    acceptKey: "auto_confirmation.accept_deck",
  },
  [EAutoCapability.IMAGE_GENERATION]: {
    descriptionKey: "auto_confirmation.image",
    acceptKey: "auto_confirmation.accept_image",
  },
  [EAutoCapability.HTML_PAGE]: {
    descriptionKey: "auto_confirmation.html_page",
    acceptKey: "auto_confirmation.accept_html_page",
  },
};

// NOTE: NSAI-6542 — the card's copy per capability (keys in the `chat`
// namespace, en / ru / ar). A capability without its own copy uses the
// generic text, so a new server-side policy never renders a blank card.
export const getAutoConfirmationCopyKeys = ({
  capability,
}: IGetAutoConfirmationCopyKeysProps): IAutoConfirmationCopyKeys =>
  AUTO_CONFIRMATION_COPY[capability] ?? {
    descriptionKey: "auto_confirmation.generic",
    acceptKey: "auto_confirmation.accept",
  };

// NOTE: Clears the pending card when the server resolves it — but only that
// card: a stale resolution for an older card must not hide a newer one.
export const reduceAutoConfirmationResolved = ({
  pending,
  resolved,
}: IReduceAutoConfirmationProps): IAutoConfirmationRequestPayload | null =>
  pending?.confirmationId === resolved.confirmationId ? null : pending;

export const isAutoConfirmationForChat = ({
  request,
  chatId,
}: IIsAutoConfirmationForChatProps): boolean =>
  request !== null && Boolean(chatId) && request.chatUuid === chatId;

// NOTE: A card whose timeout passed while the client was offline gets no
// resolution (the server already dropped it), so the client clears it itself
// at `expiresAt`, plus a grace period for the server's own resolution to win.
export const AUTO_CONFIRMATION_EXPIRY_GRACE_MS = 2_000;

export const getAutoConfirmationExpiryDelayMs = ({
  request,
  now,
}: IGetAutoConfirmationExpiryDelayProps): number => {
  const expiresAt = Date.parse(request.expiresAt);
  if (Number.isNaN(expiresAt)) return 0;
  return Math.max(0, expiresAt + AUTO_CONFIRMATION_EXPIRY_GRACE_MS - now);
};
